import React, { useState, useEffect, useRef } from 'react';
import {
  collection, query, where, orderBy, onSnapshot, doc, getDoc, updateDoc,
  deleteDoc, addDoc, setDoc, serverTimestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { signOut } from 'firebase/auth';
import { db, auth } from '../firebase-config';
import { useAuth } from '../context/AuthContext';
import { MODULES } from '../data/curriculum';
import AdminPromptLab from './AdminPromptLab';

const ADMIN_EMAILS = ['admin@theoperatormethod.com', 'rmcmanus@ramdesignworks.com', 'r0dmac522@gmail.com'];

function isAdminEmail(email) {
  const normalized = String(email || '').trim().toLowerCase();
  return ADMIN_EMAILS.includes(normalized);
}

// ─── Beta Access Panel ───────────────────────────────────────────────────────
const TIER_COLORS = {
  foundations: { bg: '#0f2233', text: '#38bdf8', label: 'Foundations' },
  operator:    { bg: '#1e3a1e', text: '#34d399', label: 'Operator' },
  certified:   { bg: '#2d1e3a', text: '#a78bfa', label: 'Certified' },
  beta:        { bg: '#1e2a3a', text: '#4f8ef7', label: 'Beta' },
};
const TIER_OPTIONS = ['foundations', 'operator', 'certified', 'beta'];

function fmtDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function fmtDateTime(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
// Returns color + dot indicator based on days since last login
function lastLoginMeta(ts) {
  if (!ts) return { color: '#3a4560', dot: '○', label: 'never' };
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const days = (Date.now() - d.getTime()) / 86400000;
  if (days <= 2)  return { color: '#4ade80', dot: '●', label: null }; // green  — active
  if (days <= 6)  return { color: '#fbbf24', dot: '●', label: null }; // amber — cooling
  return           { color: '#f87171', dot: '●', label: null };        // red   — cold
}

function BetaPanel() {
  const [form, setForm] = useState({ name: '', email: '', tier: 'foundations' });
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [grantedUsers, setGrantedUsers] = useState([]);
  const [chatSessions, setChatSessions] = useState({});
  const [revokeState, setRevokeState] = useState({});
  const [resendState, setResendState] = useState({});
  const [tierSaving, setTierSaving] = useState({});

  // Query without orderBy to avoid needing a composite index — sort client-side
  useEffect(() => {
    const q = query(collection(db, 'users'), where('isBeta', '==', true));
    const unsub = onSnapshot(q, snap => {
      const users = snap.docs.map(d => ({ uid: d.id, ...d.data() }));
      users.sort((a, b) => {
        const ta = a.betaGrantedAt?.toMillis?.() || 0;
        const tb = b.betaGrantedAt?.toMillis?.() || 0;
        return tb - ta;
      });
      setGrantedUsers(users);
    }, () => {});
    return unsub;
  }, []);

  // Fetch chat session data for each granted user
  useEffect(() => {
    if (!grantedUsers.length) return;
    let cancelled = false;
    async function fetchSessions() {
      const results = {};
      await Promise.all(grantedUsers.map(async u => {
        try {
          const snap = await getDoc(doc(db, 'chat_sessions', `${u.uid}-block-1`));
          if (snap.exists()) results[u.uid] = snap.data();
        } catch {}
      }));
      if (!cancelled) setChatSessions(results);
    }
    fetchSessions();
    return () => { cancelled = true; };
  }, [grantedUsers]);

  async function handleGrant(e) {
    e.preventDefault();
    if (!form.email) return;
    setStatus('loading');
    setMessage('');
    try {
      const fns = getFunctions();
      const grantBetaAccess = httpsCallable(fns, 'grantBetaAccess');
      const result = await grantBetaAccess({ email: form.email, name: form.name, tier: form.tier });
      setStatus('success');
      const link = result.data?.resetLink ? ` Link: ${result.data.resetLink}` : '';
      setMessage(result.data?.emailSent
        ? `✓ Invite sent to ${form.email}. BCC copy in your inbox — forward if needed.`
        : `⚠ Access granted but email FAILED. Copy this link and send manually:${link}`
      );
      setForm({ name: '', email: '', tier: 'foundations' });
    } catch (err) {
      setStatus('error');
      setMessage(err.message || 'Something went wrong.');
    }
  }

  async function handleResendInvite(uid, email) {
    setResendState(p => ({ ...p, [uid]: 'sending' }));
    try {
      const fns = getFunctions();
      const resendBetaInvite = httpsCallable(fns, 'resendBetaInvite');
      const result = await resendBetaInvite({ uid });
      if (result.data?.emailSent) {
        setResendState(p => ({ ...p, [uid]: 'sent' }));
        setTimeout(() => setResendState(p => ({ ...p, [uid]: null })), 3000);
      } else {
        const link = result.data?.resetLink || '';
        setResendState(p => ({ ...p, [uid]: 'failed' }));
        alert(`Email failed. Share this link manually with ${email}:\n\n${link}`);
      }
    } catch (err) {
      alert(`Resend failed: ${err.message}`);
      setResendState(p => ({ ...p, [uid]: null }));
    }
  }

  async function handleRevoke(uid, email) {
    if (!window.confirm(`Permanently remove ${email}? This deletes their account from Firebase and resets their beta invite code so it can be reused.`)) return;
    setRevokeState(p => ({ ...p, [uid]: 'revoking' }));
    try {
      const fns = getFunctions();
      const revokeUserAccess = httpsCallable(fns, 'revokeUserAccess');
      await revokeUserAccess({ uid });
      setRevokeState(p => ({ ...p, [uid]: 'done' }));
    } catch (err) {
      alert(`Revoke failed: ${err.message}`);
      setRevokeState(p => ({ ...p, [uid]: 'error' }));
    }
  }

  async function handleTierChange(uid, newTier) {
    setTierSaving(p => ({ ...p, [uid]: true }));
    try {
      await updateDoc(doc(db, 'users', uid), { tier: newTier });
    } catch (err) {
      alert(`Failed to update tier: ${err.message}`);
    } finally {
      setTierSaving(p => ({ ...p, [uid]: false }));
    }
  }

  const thStyle = {
    padding: '8px 12px', color: '#4f8ef7', fontSize: '11px',
    fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
    textAlign: 'left', whiteSpace: 'nowrap', borderBottom: '2px solid #1e2a3a',
    background: '#060b14', position: 'sticky', top: 0,
  };
  const tdStyle = {
    padding: '9px 12px', fontSize: '13px', color: '#c4cde0',
    borderBottom: '1px solid #0e1828', verticalAlign: 'middle', whiteSpace: 'nowrap',
  };

  return (
    <div style={{ padding: '0 40px 40px' }}>
      {/* Grant form */}
      <div style={{ ...styles.panelCard, maxWidth: '520px' }}>
        <div style={styles.panelHeading}>Grant Access</div>
        <p style={styles.panelSub}>
          Sets the student's tier in Firestore immediately. New accounts get an invite email; existing accounts do not.
        </p>
        <form onSubmit={handleGrant} style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 160px' }}>
            <label style={styles.fieldLabel}>Name <span style={{ color: '#3a4560' }}>(optional)</span></label>
            <input style={styles.input} type="text" placeholder="Full name" value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
          </div>
          <div style={{ flex: '2 1 200px' }}>
            <label style={styles.fieldLabel}>Email <span style={{ color: '#f87171' }}>*</span></label>
            <input style={styles.input} type="email" placeholder="student@example.com" value={form.email}
              onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required />
          </div>
          <div style={{ flex: '1 1 160px' }}>
            <label style={styles.fieldLabel}>Tier</label>
            <select style={styles.input} value={form.tier} onChange={e => setForm(p => ({ ...p, tier: e.target.value }))}>
              <option value="foundations">Foundations</option>
              <option value="operator">Operator</option>
              <option value="certified">Certified</option>
            </select>
          </div>
          <button type="submit" disabled={status === 'loading'}
            style={{ ...styles.approveBtn, height: '38px', flex: '0 0 auto', opacity: status === 'loading' ? 0.5 : 1 }}>
            {status === 'loading' ? 'Granting...' : 'Grant Access'}
          </button>
        </form>
        {message && (
          <div style={{ marginTop: '12px', fontSize: '13px', lineHeight: '1.6',
            color: status === 'success' ? '#34d399' : '#f87171',
            backgroundColor: status === 'success' ? '#0d2a1e' : '#2a0d0d',
            border: `1px solid ${status === 'success' ? '#34d39944' : '#f8717144'}`,
            borderRadius: '6px', padding: '10px 14px' }}>
            {message}
          </div>
        )}
      </div>

      {/* Grid */}
      <div style={{ marginTop: '28px' }}>
        <div style={{ fontSize: '12px', letterSpacing: '0.1em', textTransform: 'uppercase',
          color: '#4f8ef7', marginBottom: '12px' }}>
          Granted Access ({grantedUsers.length})
        </div>

        {grantedUsers.length === 0 && (
          <div style={{ color: '#3a4560', fontSize: '14px' }}>No users granted access yet.</div>
        )}

        {grantedUsers.length > 0 && (
          <div style={{ overflowX: 'auto', border: '1px solid #1e2a3a', borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Date Granted</th>
                  <th style={thStyle}>Level</th>
                  <th style={thStyle}>Last Login</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Demo</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Chats</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Level</th>
                  <th style={thStyle}>Last Chat</th>
                  <th style={thStyle}>Session PDF</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {grantedUsers.map((u, i) => {
                  const isRevoking = revokeState[u.uid] === 'revoking';
                  const rowBg = i % 2 === 0 ? '#060b14' : '#08101c';
                  return (
                    <tr key={u.uid} style={{ backgroundColor: rowBg }}>
                      <td style={tdStyle}>{u.name || <span style={{ color: '#3a4560' }}>—</span>}</td>
                      <td style={{ ...tdStyle, color: '#f0f4ff' }}>{u.email}</td>
                      <td style={tdStyle}>{fmtDate(u.betaGrantedAt)}</td>
                      <td style={tdStyle}>
                        <select
                          value={u.tier || 'foundations'}
                          disabled={!!tierSaving[u.uid]}
                          onChange={e => handleTierChange(u.uid, e.target.value)}
                          style={{
                            fontSize: '11px',
                            fontWeight: 600,
                            padding: '3px 8px',
                            borderRadius: '999px',
                            border: `1px solid ${(TIER_COLORS[u.tier] || TIER_COLORS.foundations).text}55`,
                            backgroundColor: (TIER_COLORS[u.tier] || TIER_COLORS.foundations).bg,
                            color: tierSaving[u.uid] ? '#3a4560' : (TIER_COLORS[u.tier] || TIER_COLORS.foundations).text,
                            cursor: tierSaving[u.uid] ? 'not-allowed' : 'pointer',
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            outline: 'none',
                            minWidth: '90px',
                          }}
                        >
                          {TIER_OPTIONS.map(t => (
                            <option key={t} value={t} style={{ backgroundColor: '#0a0e1a', color: '#f0f4ff' }}>
                              {TIER_COLORS[t]?.label || t}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={tdStyle}>
                        {(() => {
                          const { color, dot, label } = lastLoginMeta(u.lastLoginAt);
                          return (
                            <span style={{ color, display: 'flex', alignItems: 'center', gap: '5px' }}>
                              <span style={{ fontSize: '8px' }}>{dot}</span>
                              <span>{label || fmtDateTime(u.lastLoginAt)}</span>
                            </span>
                          );
                        })()}
                      </td>
                      {(() => {
                        const cs = chatSessions[u.uid];
                        const demoComplete = !!u.savedDemo?.demoComplete;
                        const lessonCount = cs?.lessonCount ?? 0;
                        const level = cs?.assessedLevel;
                        const lastChat = cs?.lastMessage;
                        return (
                          <>
                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                              <span style={{ color: demoComplete ? '#34d399' : '#3a4560', fontWeight: 700 }}>
                                {demoComplete ? '✓' : '—'}
                              </span>
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'center', fontFamily: 'monospace',
                              color: lessonCount > 0 ? '#4f8ef7' : '#3a4560', fontWeight: 600 }}>
                              {lessonCount}/4
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'center', fontSize: '11px',
                              color: level ? '#a78bfa' : '#3a4560', fontWeight: level ? 600 : 400 }}>
                              {level || '—'}
                            </td>
                            <td style={tdStyle}>
                              {lastChat ? (
                                <span style={{ color: '#c4cde0', fontSize: '12px' }}>{fmtDateTime(lastChat)}</span>
                              ) : (
                                <span style={{ color: '#3a4560' }}>—</span>
                              )}
                            </td>
                          </>
                        );
                      })()}
                      <td style={tdStyle}>
                        {u.latestSessionPdf ? (
                          <a
                            href={u.latestSessionPdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#00d4ff', fontSize: '12px', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}
                          >
                            ↓ View PDF
                          </a>
                        ) : (
                          <span style={{ color: '#2a3550', fontSize: '12px' }}>—</span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, display: 'flex', gap: '6px', alignItems: 'center' }}>
                        {(() => {
                          const rs = resendState[u.uid];
                          return (
                            <button onClick={() => handleResendInvite(u.uid, u.email)} disabled={rs === 'sending'}
                              style={{ background: 'none', border: '1px solid #1e3a5f', borderRadius: '5px',
                                color: rs === 'sent' ? '#4ade80' : '#4f8ef7', fontSize: '11px', padding: '4px 10px',
                                cursor: rs === 'sending' ? 'not-allowed' : 'pointer', opacity: rs === 'sending' ? 0.5 : 1 }}>
                              {rs === 'sending' ? '...' : rs === 'sent' ? '✓ Sent' : 'Resend'}
                            </button>
                          );
                        })()}
                        <button onClick={() => handleRevoke(u.uid, u.email)} disabled={isRevoking}
                          style={{ background: 'none', border: '1px solid #450a0a', borderRadius: '5px',
                            color: '#f87171', fontSize: '11px', padding: '4px 10px',
                            cursor: isRevoking ? 'not-allowed' : 'pointer', opacity: isRevoking ? 0.5 : 1 }}>
                          {isRevoking ? '...' : 'Remove'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Beta Invites Panel ─────────────────────────────────────────────────────
function BetaInvitesPanel() {
  const [form, setForm] = useState({ name: '', email: '' });
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [invites, setInvites] = useState([]);
  const [betaUsersByEmail, setBetaUsersByEmail] = useState({});
  const [deleteState, setDeleteState] = useState({});
  const [resendState, setResendState] = useState({});

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'beta_invites'), orderBy('sentAt', 'desc')),
      snap => setInvites(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => {}
    );
    return unsub;
  }, []);

  // Cross-reference users collection so we catch cases where the invite used
  // flag wasn't flipped (e.g. direct grantBetaAccess path)
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'users'), where('isBeta', '==', true)),
      snap => {
        const map = {};
        snap.docs.forEach(d => {
          const data = d.data();
          if (data.email) map[data.email.toLowerCase()] = data;
        });
        setBetaUsersByEmail(map);
      },
      () => {}
    );
    return unsub;
  }, []);

  async function handleSend(e) {
    e.preventDefault();
    if (!form.email) return;
    setStatus('loading');
    setMessage('');
    try {
      const fns = getFunctions();
      const sendBetaInvite = httpsCallable(fns, 'sendBetaInvite');
      const result = await sendBetaInvite({ email: form.email, name: form.name });
      setStatus('success');
      setMessage(result.data?.message || 'Invite sent.');
      setForm({ name: '', email: '' });
    } catch (err) {
      setStatus('error');
      setMessage(err.message || 'Something went wrong.');
    }
  }

  async function handleDeleteInvite(id, email) {
    if (!window.confirm(`Delete invite for ${email}? This cannot be undone.`)) return;
    setDeleteState(p => ({ ...p, [id]: 'deleting' }));
    try {
      await deleteDoc(doc(db, 'beta_invites', id));
    } catch (err) {
      alert(`Delete failed: ${err.message}`);
    }
    setDeleteState(p => ({ ...p, [id]: null }));
  }

  async function handleResendInvite(id, email, name) {
    setResendState(p => ({ ...p, [id]: 'sending' }));
    try {
      const fns = getFunctions();
      const resendInviteByCode = httpsCallable(fns, 'resendInviteByCode');
      await resendInviteByCode({ code: id });
      setResendState(p => ({ ...p, [id]: 'sent' }));
      setTimeout(() => setResendState(p => ({ ...p, [id]: null })), 3000);
    } catch (err) {
      alert(`Resend failed: ${err.message}`);
      setResendState(p => ({ ...p, [id]: null }));
    }
  }

  const thStyle = {
    padding: '8px 12px', color: '#4f8ef7', fontSize: '11px',
    fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
    textAlign: 'left', whiteSpace: 'nowrap', borderBottom: '2px solid #1e2a3a',
    background: '#060b14', position: 'sticky', top: 0,
  };
  const tdStyle = {
    padding: '9px 12px', fontSize: '13px', color: '#c4cde0',
    borderBottom: '1px solid #0e1828', verticalAlign: 'middle', whiteSpace: 'nowrap',
  };

  const pending  = invites.filter(i => !i.used).length;
  const redeemed = invites.filter(i =>  i.used).length;

  return (
    <div style={{ padding: '0 40px 40px' }}>
      {/* Send invite form */}
      <div style={{ ...styles.panelCard, maxWidth: '520px', marginBottom: '32px' }}>
        <div style={styles.panelHeading}>Send Beta Invite</div>
        <p style={styles.panelSub}>
          Generates a unique promo code, stores it in Firestore, and emails the invite with a link to the diagnostic and their personal code.
        </p>
        <form onSubmit={handleSend} style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 160px' }}>
            <label style={styles.fieldLabel}>Name <span style={{ color: '#3a4560' }}>(optional)</span></label>
            <input style={styles.input} type="text" placeholder="Full name" value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
          </div>
          <div style={{ flex: '2 1 200px' }}>
            <label style={styles.fieldLabel}>Email <span style={{ color: '#f87171' }}>*</span></label>
            <input style={styles.input} type="email" placeholder="invitee@example.com" value={form.email}
              onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required />
          </div>
          <button type="submit" disabled={status === 'loading'}
            style={{ ...styles.approveBtn, height: '38px', flex: '0 0 auto', opacity: status === 'loading' ? 0.5 : 1 }}>
            {status === 'loading' ? 'Sending...' : 'Send Invite'}
          </button>
        </form>
        {message && (
          <div style={{ marginTop: '12px', fontSize: '13px', lineHeight: '1.6',
            color: status === 'success' ? '#34d399' : '#f87171',
            backgroundColor: status === 'success' ? '#0d2a1e' : '#2a0d0d',
            border: `1px solid ${status === 'success' ? '#34d39944' : '#f8717144'}`,
            borderRadius: '6px', padding: '10px 14px' }}>
            {message}
          </div>
        )}
      </div>

      {/* Invite summary */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
        {[{ label: 'Sent', val: invites.length, color: '#4f8ef7' },
          { label: 'Pending', val: pending, color: '#fbbf24' },
          { label: 'Redeemed', val: redeemed, color: '#34d399' },
        ].map(({ label, val, color }) => (
          <div key={label} style={{ background: '#0d1220', border: `1px solid ${color}33`,
            borderRadius: '10px', padding: '12px 20px', textAlign: 'center', minWidth: '80px' }}>
            <div style={{ fontSize: '22px', fontWeight: 700, color }}>{val}</div>
            <div style={{ fontSize: '11px', color: '#4a5568', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Invites grid */}
      {invites.length > 0 && (
        <div style={{ overflowX: 'auto', border: '1px solid #1e2a3a', borderRadius: '8px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
            <thead>
              <tr>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Code</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Sent</th>
                <th style={thStyle}>Redeemed</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {invites.map((inv, i) => (
                <tr key={inv.id} style={{ backgroundColor: i % 2 === 0 ? '#060b14' : '#08101c' }}>
                  <td style={{ ...tdStyle, color: '#f0f4ff' }}>{inv.email}</td>
                  <td style={tdStyle}>{inv.name || <span style={{ color: '#3a4560' }}>—</span>}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', color: '#00d4ff', letterSpacing: '0.05em' }}>{inv.code}</td>
                  <td style={tdStyle}>
                    {(() => {
                      const matchedUser = betaUsersByEmail[(inv.email || '').toLowerCase()];
                      const isRedeemed = inv.used || (matchedUser && !!matchedUser.tier);
                      const s = isRedeemed
                        ? { bg: '#0d2a1e', color: '#34d399', border: '#34d39944', label: 'Redeemed' }
                        : inv.pendingRedemption
                          ? { bg: '#1e1a0d', color: '#fb923c', border: '#fb923c44', label: 'Code Entered' }
                          : { bg: '#1e2a0d', color: '#fbbf24', border: '#fbbf2444', label: 'Pending' };
                      return (
                        <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 10px',
                          borderRadius: '999px', background: s.bg, color: s.color,
                          border: `1px solid ${s.border}` }}>
                          {s.label}
                        </span>
                      );
                    })()}
                  </td>
                  <td style={tdStyle}>{fmtDate(inv.sentAt)}</td>
                  <td style={tdStyle}>{inv.used ? fmtDateTime(inv.usedAt) : <span style={{ color: '#3a4560' }}>—</span>}</td>
                  <td style={{ ...tdStyle, display: 'flex', gap: '6px', alignItems: 'center' }}>
                    {(() => {
                      const rs = resendState[inv.id];
                      return (
                        <button onClick={() => handleResendInvite(inv.id, inv.email, inv.name)} disabled={rs === 'sending'}
                          style={{ background: 'none', border: '1px solid #1e3a5f', borderRadius: '5px',
                            color: rs === 'sent' ? '#4ade80' : '#4f8ef7', fontSize: '11px', padding: '4px 10px',
                            cursor: rs === 'sending' ? 'not-allowed' : 'pointer', opacity: rs === 'sending' ? 0.5 : 1 }}>
                          {rs === 'sending' ? '...' : rs === 'sent' ? '✓ Sent' : 'Resend'}
                        </button>
                      );
                    })()}
                    <button onClick={() => handleDeleteInvite(inv.id, inv.email)}
                      disabled={deleteState[inv.id] === 'deleting'}
                      style={{ background: 'none', border: '1px solid #450a0a', borderRadius: '5px',
                        color: '#f87171', fontSize: '11px', padding: '4px 10px',
                        cursor: deleteState[inv.id] === 'deleting' ? 'not-allowed' : 'pointer',
                        opacity: deleteState[inv.id] === 'deleting' ? 0.5 : 1 }}>
                      {deleteState[inv.id] === 'deleting' ? '...' : 'Delete'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {invites.length === 0 && (
        <div style={{ color: '#3a4560', fontSize: '14px' }}>No invites sent yet.</div>
      )}
    </div>
  );
}

// ─── Feedback Panel ───────────────────────────────────────────────────────────
const SURVEY_COLS = [
  { key: 'q1_visualAppeal',       short: 'Q1 Visual' },
  { key: 'q2_websiteFunction',    short: 'Q2 Function' },
  { key: 'q3_chatboxInteraction', short: 'Q3 Chat' },
  { key: 'q4_learnedAboutAI',     short: 'Q4 Learned' },
  { key: 'q5_priceValue',         short: 'Q5 Price' },
];

function scoreBadgeStyle(v) {
  if (!v) return { background: 'rgba(255,255,255,0.06)', color: '#475569' };
  if (v <= 2) return { background: 'rgba(239,68,68,0.18)', color: '#f87171' };
  if (v === 3) return { background: 'rgba(245,158,11,0.18)', color: '#fbbf24' };
  return { background: 'rgba(34,197,94,0.15)', color: '#4ade80' };
}

function ScoreBadge({ v }) {
  const s = scoreBadgeStyle(v);
  return (
    <span style={{
      display: 'inline-block', minWidth: '28px', textAlign: 'center',
      padding: '3px 8px', borderRadius: '6px', fontSize: '13px', fontWeight: 700,
      ...s,
    }}>
      {v || '—'}
    </span>
  );
}

function FeedbackPanel() {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [expandedQ6, setExpandedQ6] = useState(null);
  const [busyIds, setBusyIds] = useState(new Set());
  const [showAcknowledged, setShowAcknowledged] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'feedback'), snap => {
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => (b.submittedAt?.toMillis?.() ?? 0) - (a.submittedAt?.toMillis?.() ?? 0));
      setFeedback(rows);
      setFetchError(null);
      setLoading(false);
    }, err => {
      console.error('[FeedbackPanel] Firestore error:', err.code, err.message);
      setFetchError(`${err.code}: ${err.message}`);
      setLoading(false);
    });
    return unsub;
  }, []);

  async function acknowledgeEntry(id) {
    setBusyIds(prev => new Set(prev).add(id));
    try { await updateDoc(doc(db, 'feedback', id), { acknowledged: true, acknowledgedAt: serverTimestamp() }); }
    catch (e) { console.error(e); }
    setBusyIds(prev => { const s = new Set(prev); s.delete(id); return s; });
  }

  async function deleteEntry(id) {
    if (!window.confirm('Permanently delete this feedback entry?')) return;
    setBusyIds(prev => new Set(prev).add(id));
    try { await deleteDoc(doc(db, 'feedback', id)); }
    catch (e) { console.error(e); }
    setBusyIds(prev => { const s = new Set(prev); s.delete(id); return s; });
  }

  async function clearAllAcknowledged() {
    const acked = feedback.filter(f => f.acknowledged);
    if (!acked.length) { alert('No acknowledged entries to clear.'); return; }
    if (!window.confirm(`Permanently delete ${acked.length} acknowledged entr${acked.length === 1 ? 'y' : 'ies'}?`)) return;
    setClearingAll(true);
    try { await Promise.all(acked.map(f => deleteDoc(doc(db, 'feedback', f.id)))); }
    catch (e) { console.error(e); }
    setClearingAll(false);
  }

  function formatDate(ts) {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  function avg(key) {
    const vals = feedback.filter(f => f[key] > 0).map(f => f[key]);
    if (!vals.length) return null;
    return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
  }

  const ackedCount = feedback.filter(f => f.acknowledged).length;
  const visibleFeedback = showAcknowledged ? feedback : feedback.filter(f => !f.acknowledged);
  const v2 = visibleFeedback.filter(f => f.surveyVersion === 2);
  const legacy = visibleFeedback.filter(f => f.surveyVersion !== 2);

  const btnBase = { border: 'none', borderRadius: '5px', fontSize: '11px', padding: '3px 8px', cursor: 'pointer' };

  return (
    <div style={{ padding: '0 40px 40px' }}>

      {/* ── Header row ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
        <div style={{ fontSize: '12px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#4f8ef7', flex: 1 }}>
          Feedback ({feedback.length}{ackedCount > 0 ? ` · ${ackedCount} acknowledged` : ''})
        </div>
        <button
          onClick={() => setShowAcknowledged(p => !p)}
          style={{ ...btnBase, background: 'none', border: '1px solid #1e2a3a', color: '#4a6a80', padding: '4px 12px' }}
        >
          {showAcknowledged ? 'Hide Acknowledged' : 'Show Acknowledged'}
        </button>
        {ackedCount > 0 && (
          <button
            onClick={clearAllAcknowledged}
            disabled={clearingAll}
            style={{ ...btnBase, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171', padding: '4px 12px' }}
          >
            {clearingAll ? 'Clearing…' : `Clear ${ackedCount} Acknowledged`}
          </button>
        )}
      </div>

      {loading && <div style={styles.empty}>Loading feedback…</div>}
      {!loading && fetchError && (
        <div style={{ ...styles.empty, color: '#f87171', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '8px', padding: '16px 20px', fontFamily: 'monospace', fontSize: '13px' }}>
          ⚠ Firestore read error — {fetchError}
        </div>
      )}
      {!loading && !fetchError && visibleFeedback.length === 0 && (
        <div style={styles.empty}>
          {feedback.length === 0 ? 'No feedback submitted yet.' : 'All entries acknowledged — click "Show Acknowledged" to review.'}
        </div>
      )}

      {/* ── V2 structured survey entries ── */}
      {v2.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
            <div style={{ fontSize: '13px', color: '#4a5568', alignSelf: 'center' }}>
              <span style={{ color: '#f0f4ff', fontWeight: 700 }}>{v2.length}</span> survey{v2.length !== 1 ? 's' : ''} · avg scores:
            </div>
            {SURVEY_COLS.map(col => (
              <div key={col.key} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#4a5568' }}>
                <span>{col.short}</span>
                <ScoreBadge v={avg(col.key) ? parseFloat(avg(col.key)) : null} />
              </div>
            ))}
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', color: '#c4d0e8' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                  <th style={thStyle}>Respondent</th>
                  <th style={thStyle}>Date</th>
                  {SURVEY_COLS.map(c => (
                    <th key={c.key} style={{ ...thStyle, textAlign: 'center' }}>{c.short}</th>
                  ))}
                  <th style={thStyle}>Q6 Comments</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {v2.map(item => {
                  const q6 = item.q6_overallImpression;
                  const isExpanded = expandedQ6 === item.id;
                  const isAcked = !!item.acknowledged;
                  const isBusy = busyIds.has(item.id);
                  return (
                    <tr key={item.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', opacity: isAcked ? 0.45 : 1 }}>
                      <td style={tdStyle}>
                        <span style={{ color: '#4f8ef7', fontWeight: 600 }}>
                          {item.email || item.userId || 'Anonymous'}
                        </span>
                        {item.moduleName && (
                          <div style={{ fontSize: '11px', color: '#3a4560', marginTop: '2px' }}>{item.moduleName}</div>
                        )}
                        {isAcked && <div style={{ fontSize: '10px', color: '#2a3550', marginTop: '2px' }}>acknowledged</div>}
                      </td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap', color: '#3a4560', fontSize: '12px' }}>
                        {formatDate(item.submittedAt)}
                      </td>
                      {SURVEY_COLS.map(c => (
                        <td key={c.key} style={{ ...tdStyle, textAlign: 'center' }}>
                          <ScoreBadge v={item[c.key]} />
                        </td>
                      ))}
                      <td style={tdStyle}>
                        {q6 ? (
                          <div>
                            <span style={{ color: '#c4d0e8', lineHeight: 1.5 }}>
                              {isExpanded ? q6 : q6.slice(0, 80) + (q6.length > 80 ? '…' : '')}
                            </span>
                            {q6.length > 80 && (
                              <button
                                onClick={() => setExpandedQ6(isExpanded ? null : item.id)}
                                style={{ background: 'none', border: 'none', color: '#4f8ef7', fontSize: '11px', cursor: 'pointer', padding: '0 4px', marginLeft: '4px' }}
                              >
                                {isExpanded ? 'less' : 'more'}
                              </button>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: '#2a3550', fontStyle: 'italic' }}>—</span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                        {!isAcked && (
                          <button
                            onClick={() => acknowledgeEntry(item.id)}
                            disabled={isBusy}
                            style={{ ...btnBase, background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.2)', color: '#34d399', marginRight: '6px' }}
                          >
                            {isBusy ? '…' : 'Ack'}
                          </button>
                        )}
                        <button
                          onClick={() => deleteEntry(item.id)}
                          disabled={isBusy}
                          style={{ ...btnBase, background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', color: '#f87171' }}
                        >
                          {isBusy ? '…' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Legacy entries ── */}
      {legacy.length > 0 && (
        <>
          <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', color: '#3a4560', textTransform: 'uppercase', margin: '32px 0 12px' }}>
            Legacy Entries ({legacy.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {legacy.map(item => {
              const isAcked = !!item.acknowledged;
              const isBusy = busyIds.has(item.id);
              return (
                <div key={item.id} style={{ ...styles.feedbackCard, opacity: isAcked ? 0.45 : 1 }}>
                  <div style={styles.feedbackMeta}>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ color: '#4f8ef7', fontSize: '14px', fontWeight: 600 }}>{item.email || 'Anonymous'}</span>
                      {item.rating != null && (
                        <span style={styles.ratingBadge}>{'★'.repeat(item.rating)}{'☆'.repeat(5 - item.rating)}</span>
                      )}
                      {item.lesson && <span style={styles.lessonTag}>{item.lesson}</span>}
                      {isAcked && <span style={{ fontSize: '10px', color: '#2a3550', fontStyle: 'italic' }}>acknowledged</span>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '12px', color: '#3a4560' }}>{formatDate(item.submittedAt)}</span>
                      {!isAcked && (
                        <button
                          onClick={() => acknowledgeEntry(item.id)}
                          disabled={isBusy}
                          style={{ ...btnBase, background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.2)', color: '#34d399' }}
                        >
                          {isBusy ? '…' : 'Ack'}
                        </button>
                      )}
                      <button
                        onClick={() => deleteEntry(item.id)}
                        disabled={isBusy}
                        style={{ ...btnBase, background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', color: '#f87171' }}
                      >
                        {isBusy ? '…' : 'Delete'}
                      </button>
                    </div>
                  </div>
                  {item.message && <div style={styles.feedbackMessage}>"{item.message}"</div>}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

const thStyle = { padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', color: '#3a4560', textTransform: 'uppercase', whiteSpace: 'nowrap' };
const tdStyle = { padding: '10px 12px', verticalAlign: 'top' };

// ─── Flagged Accounts Panel ───────────────────────────────────────────────────
function FlaggedPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // uid of expanded row

  useEffect(() => {
    const q = query(
      collection(db, 'users'),
      where('flagged', '==', true)
    );
    const unsub = onSnapshot(q, snap => {
      setUsers(snap.docs.map(d => ({ uid: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  async function clearFlag(uid) {
    await updateDoc(doc(db, 'users', uid), { flagged: false });
  }

  function formatTS(ts) {
    if (!ts) return '?';
    const d = new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  return (
    <div style={{ padding: '0 40px 40px', maxWidth: '900px' }}>
      <div style={{ marginBottom: '20px', color: '#94a3b8', fontSize: '14px', lineHeight: '1.6' }}>
        Accounts flagged for <strong style={{ color: '#fbbf24' }}>frequent IP changes</strong> — likely shared logins.
        IP history shown below each account. Clear the flag once reviewed.
      </div>

      {loading && <div style={styles.empty}>Loading...</div>}
      {!loading && users.length === 0 && (
        <div style={styles.empty}>No flagged accounts. 🎉</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {users.map(u => (
          <div key={u.uid} style={{
            backgroundColor: '#0f172a',
            border: '1px solid #f59e0b44',
            borderRadius: '10px',
            overflow: 'hidden',
          }}>
            {/* Header row */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 18px', flexWrap: 'wrap', gap: '10px',
            }}>
              <div>
                <div style={{ color: '#fbbf24', fontWeight: 700, fontSize: '15px' }}>
                  {u.email || u.uid}
                  {u.tier && (
                    <span style={{ marginLeft: '8px', fontSize: '11px', backgroundColor: '#1e293b', color: '#94a3b8', padding: '2px 8px', borderRadius: '999px' }}>
                      {u.tier}
                    </span>
                  )}
                </div>
                <div style={{ color: '#64748b', fontSize: '12px', marginTop: '3px' }}>
                  Current IP: <strong style={{ color: '#e2e8f0' }}>{u.currentIP || '—'}</strong>
                  &nbsp;&nbsp;·&nbsp;&nbsp;
                  IP changes: <strong style={{ color: '#f87171' }}>{u.ipChangeCount || 0}</strong>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setExpanded(expanded === u.uid ? null : u.uid)}
                  style={{ ...styles.denyBtn, backgroundColor: '#1e293b', borderColor: '#334155', color: '#94a3b8', fontSize: '12px', padding: '6px 12px' }}
                >
                  {expanded === u.uid ? 'Hide History' : 'IP History'}
                </button>
                <button
                  onClick={() => clearFlag(u.uid)}
                  style={{ ...styles.approveBtn, fontSize: '12px', padding: '6px 12px' }}
                >
                  Clear Flag
                </button>
              </div>
            </div>

            {/* IP history */}
            {expanded === u.uid && (
              <div style={{ borderTop: '1px solid #1e293b', padding: '14px 18px', backgroundColor: '#060b14' }}>
                {(!u.ipHistory || u.ipHistory.length === 0) ? (
                  <div style={{ color: '#475569', fontSize: '13px' }}>No history logged.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ color: '#475569', textAlign: 'left' }}>
                        <th style={{ paddingBottom: '8px', paddingRight: '24px' }}>#</th>
                        <th style={{ paddingBottom: '8px', paddingRight: '24px' }}>IP Address</th>
                        <th style={{ paddingBottom: '8px', paddingRight: '24px' }}>When</th>
                        <th style={{ paddingBottom: '8px' }}>User Agent (truncated)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...u.ipHistory].reverse().map((entry, i) => (
                        <tr key={i} style={{ borderTop: '1px solid #0f172a' }}>
                          <td style={{ padding: '6px 24px 6px 0', color: '#475569' }}>{u.ipHistory.length - i}</td>
                          <td style={{ padding: '6px 24px 6px 0', color: '#e2e8f0', fontFamily: 'monospace' }}>{entry.ip}</td>
                          <td style={{ padding: '6px 24px 6px 0', color: '#94a3b8', whiteSpace: 'nowrap' }}>{formatTS(entry.ts)}</td>
                          <td style={{ padding: '6px 0', color: '#475569', fontSize: '11px', maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.ua}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Security Log Panel ──────────────────────────────────────────────────────
function SecurityPanel() {
  const { currentUser } = useAuth();
  const [logs, setLogs] = useState([]);
  const [audits, setAudits] = useState([]);
  const [inactivityHours, setInactivityHours] = useState(4);
  const [hoursLoading, setHoursLoading] = useState(true);
  const [hoursSaving, setHoursSaving] = useState(false);
  const [hoursStatus, setHoursStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [auditsLoading, setAuditsLoading] = useState(true);
  const [showAcknowledged, setShowAcknowledged] = useState(false);
  const [busyIds, setBusyIds] = useState(new Set());
  const [clearingAll, setClearingAll] = useState(false);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'security_logs'), orderBy('timestamp', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setLogs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, err => {
      console.error('[SecurityPanel] logs error:', err.code);
      setLoading(false);
    });
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'app_config', 'session_policy'), (snap) => {
      const parsed = Number(snap.data()?.inactivityLogoutHours);
      if (Number.isFinite(parsed)) {
        setInactivityHours(Math.min(24, Math.max(1, parsed)));
      } else {
        setInactivityHours(4);
      }
      setHoursLoading(false);
    }, () => {
      setHoursLoading(false);
    });
    return unsub;
  }, []);

  async function saveInactivityHours() {
    const normalized = Math.min(24, Math.max(1, Number(inactivityHours) || 4));
    setHoursSaving(true);
    setHoursStatus('');
    try {
      await setDoc(doc(db, 'app_config', 'session_policy'), {
        inactivityLogoutHours: normalized,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.email || 'admin',
      }, { merge: true });
      setHoursStatus('Saved');
    } catch {
      setHoursStatus('Unable to save');
    } finally {
      setHoursSaving(false);
    }
  }

  async function acknowledgeLog(id) {
    setBusyIds(prev => new Set(prev).add(id));
    try {
      await updateDoc(doc(db, 'security_logs', id), { acknowledged: true, acknowledgedAt: serverTimestamp() });
    } catch (e) { console.error(e); }
    setBusyIds(prev => { const s = new Set(prev); s.delete(id); return s; });
  }

  async function deleteLog(id) {
    if (!window.confirm('Permanently delete this security event?')) return;
    setBusyIds(prev => new Set(prev).add(id));
    try { await deleteDoc(doc(db, 'security_logs', id)); }
    catch (e) { console.error(e); }
    setBusyIds(prev => { const s = new Set(prev); s.delete(id); return s; });
  }

  async function clearAllAcknowledged() {
    const acked = logs.filter(l => l.acknowledged);
    if (!acked.length) { alert('No acknowledged events to clear.'); return; }
    if (!window.confirm(`Permanently delete ${acked.length} acknowledged event(s)?`)) return;
    setClearingAll(true);
    try {
      await Promise.all(acked.map(l => deleteDoc(doc(db, 'security_logs', l.id))));
    } catch (e) { console.error(e); }
    setClearingAll(false);
  }

  async function saveAdminNote() {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      await addDoc(collection(db, 'security_logs'), {
        event: 'ADMIN_NOTE',
        note: noteText.trim(),
        timestamp: serverTimestamp(),
        doctrineVersion: '1.0',
        addedBy: 'admin@theoperatormethod.com',
      });
      setNoteText('');
      setShowNoteForm(false);
    } catch (e) { console.error(e); }
    setSavingNote(false);
  }

  useEffect(() => {
    const q = query(collection(db, 'compliance_audits'), orderBy('runAt', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setAudits(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setAuditsLoading(false);
    }, () => setAuditsLoading(false));
    return unsub;
  }, []);

  function fmtTs(ts) {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }

  const EVENT_COLOR = {
    DEMO_INJECTION_ATTEMPT:     '#f87171',
    OPERATOR_INJECTION_ATTEMPT: '#f87171',
    BETA_INJECTION_ATTEMPT:     '#f87171',
  };

  const thS = { padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', color: '#3a4560', textTransform: 'uppercase', whiteSpace: 'nowrap', borderBottom: '2px solid #1e2a3a', background: '#060b14' };
  const tdS = { padding: '10px 12px', verticalAlign: 'top', borderBottom: '1px solid #0e1828', fontSize: '13px' };

  const visibleLogs = showAcknowledged ? logs : logs.filter(l => !l.acknowledged);
  const ackedCount = logs.filter(l => l.acknowledged).length;

  return (
    <div style={{ padding: '0 40px 40px' }}>

      <div style={{ marginBottom: '14px', border: '1px solid #1e2a3a', borderRadius: '8px', padding: '14px', backgroundColor: '#08111d' }}>
        <div style={{ fontSize: '12px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#60a5fa', marginBottom: '8px' }}>
          Session Timeout
        </div>
        <div style={{ fontSize: '13px', color: '#93a6c4', marginBottom: '10px', lineHeight: 1.5 }}>
          Auto-sign users out after this many hours of inactivity.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <input
            type="number"
            min={1}
            max={24}
            step={1}
            value={inactivityHours}
            disabled={hoursLoading || hoursSaving}
            onChange={(e) => setInactivityHours(e.target.value)}
            style={{
              width: '88px',
              background: '#0a1220',
              border: '1px solid #1e2a3a',
              borderRadius: '6px',
              color: '#d6e3f8',
              fontSize: '14px',
              padding: '8px 10px',
            }}
          />
          <span style={{ fontSize: '13px', color: '#6f88ab' }}>hours</span>
          <button
            onClick={saveInactivityHours}
            disabled={hoursLoading || hoursSaving}
            style={{
              background: 'rgba(59,130,246,0.15)',
              border: '1px solid rgba(59,130,246,0.3)',
              borderRadius: '6px',
              color: '#60a5fa',
              fontSize: '12px',
              padding: '7px 12px',
              cursor: 'pointer',
            }}
          >
            {hoursSaving ? 'Saving...' : 'Save'}
          </button>
          {hoursStatus && (
            <span style={{ fontSize: '12px', color: hoursStatus === 'Saved' ? '#34d399' : '#f87171' }}>{hoursStatus}</span>
          )}
        </div>
      </div>

      {/* ── Header row ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <div style={{ fontSize: '12px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#f87171', flex: 1 }}>
          Security Events ({logs.length}{ackedCount > 0 ? ` · ${ackedCount} acknowledged` : ''})
        </div>
        <button
          onClick={() => setShowAcknowledged(p => !p)}
          style={{ background: 'none', border: '1px solid #1e2a3a', borderRadius: '6px', color: '#4a6a80', fontSize: '12px', padding: '4px 12px', cursor: 'pointer' }}
        >
          {showAcknowledged ? 'Hide Acknowledged' : 'Show Acknowledged'}
        </button>
        {ackedCount > 0 && (
          <button
            onClick={clearAllAcknowledged}
            disabled={clearingAll}
            style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '6px', color: '#f87171', fontSize: '12px', padding: '4px 12px', cursor: 'pointer' }}
          >
            {clearingAll ? 'Clearing…' : `Clear ${ackedCount} Acknowledged`}
          </button>
        )}
        <button
          onClick={() => { setShowNoteForm(p => !p); setNoteText(p => p || `IP 172.59.26.242 — reviewed Mar 1, 2026. Events confirmed as external demo user activity; not generated by AI assistant (Copilot). No action required.`); }}
          style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '6px', color: '#60a5fa', fontSize: '12px', padding: '4px 12px', cursor: 'pointer' }}
        >
          + Log Admin Note
        </button>
      </div>

      {/* ── Admin note form ── */}
      {showNoteForm && (
        <div style={{ background: '#080f1a', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
          <div style={{ fontSize: '12px', color: '#60a5fa', marginBottom: '8px', fontWeight: 700 }}>Admin Note → security_logs</div>
          <textarea
            id="admin-note"
            name="admin-note"
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            rows={3}
            style={{ width: '100%', background: '#0a1220', border: '1px solid #1e2a3a', borderRadius: '6px', color: '#c4d0e8', fontSize: '13px', padding: '10px', resize: 'vertical', boxSizing: 'border-box' }}
          />
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            <button
              onClick={saveAdminNote}
              disabled={savingNote || !noteText.trim()}
              style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: '6px', color: '#60a5fa', fontSize: '13px', padding: '6px 16px', cursor: 'pointer' }}
            >
              {savingNote ? 'Saving…' : 'Save Note'}
            </button>
            <button
              onClick={() => setShowNoteForm(false)}
              style={{ background: 'none', border: '1px solid #1e2a3a', borderRadius: '6px', color: '#4a5568', fontSize: '13px', padding: '6px 12px', cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading && <div style={styles.empty}>Loading…</div>}
      {!loading && visibleLogs.length === 0 && (
        <div style={{ color: '#3a4560', fontSize: '14px' }}>
          {logs.length === 0 ? 'No security events recorded.' : 'All events acknowledged — click "Show Acknowledged" to review.'}
        </div>
      )}
      {visibleLogs.length > 0 && (
        <div style={{ overflowX: 'auto', border: '1px solid #1e2a3a', borderRadius: '8px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
            <thead>
              <tr>
                <th style={thS}>Event</th>
                <th style={thS}>User / Session</th>
                <th style={thS}>Timestamp</th>
                <th style={thS}>Input / Note</th>
                <th style={thS}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleLogs.map(log => {
                const isBusy = busyIds.has(log.id);
                const isNote = log.event === 'ADMIN_NOTE';
                const isAcked = !!log.acknowledged;
                return (
                  <tr key={log.id} style={{ opacity: isAcked ? 0.45 : 1 }}>
                    <td style={tdS}>
                      <span style={{
                        padding: '3px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: 700,
                        background: isNote ? 'rgba(59,130,246,0.10)' : 'rgba(248,113,113,0.12)',
                        color: isNote ? '#60a5fa' : (EVENT_COLOR[log.event] || '#f87171'),
                        whiteSpace: 'nowrap',
                      }}>
                        {log.event || 'UNKNOWN'}
                      </span>
                      {isAcked && (
                        <div style={{ fontSize: '10px', color: '#2a3550', marginTop: '3px' }}>acknowledged</div>
                      )}
                    </td>
                    <td style={{ ...tdS, fontFamily: 'monospace', fontSize: '11px', color: '#4a5568' }}>
                      {log.uid && <div style={{ color: '#c4d0e8' }}>uid: {log.uid.slice(0, 12)}…</div>}
                      {log.fingerprint && <div>fp: {log.fingerprint.slice(0, 12)}…</div>}
                      {log.ip && <div>ip: {log.ip}</div>}
                      {log.addedBy && <div style={{ color: '#3a4560' }}>{log.addedBy}</div>}
                    </td>
                    <td style={{ ...tdS, whiteSpace: 'nowrap', color: '#3a4560', fontSize: '12px' }}>
                      {fmtTs(log.timestamp)}
                    </td>
                    <td style={{ ...tdS, color: isNote ? '#8aaec8' : '#f87171', fontFamily: 'monospace', fontSize: '11px', maxWidth: '280px', wordBreak: 'break-all' }}>
                      {isNote
                        ? (log.note || '—')
                        : (log.input ? `"${log.input.slice(0, 100)}${log.input.length > 100 ? '…' : ''}"` : '—')
                      }
                    </td>
                    <td style={{ ...tdS, whiteSpace: 'nowrap' }}>
                      {!isAcked && (
                        <button
                          onClick={() => acknowledgeLog(log.id)}
                          disabled={isBusy}
                          style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.2)', borderRadius: '5px', color: '#34d399', fontSize: '11px', padding: '3px 8px', cursor: 'pointer', marginRight: '6px' }}
                        >
                          {isBusy ? '…' : 'Ack'}
                        </button>
                      )}
                      <button
                        onClick={() => deleteLog(log.id)}
                        disabled={isBusy}
                        style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', borderRadius: '5px', color: '#f87171', fontSize: '11px', padding: '3px 8px', cursor: 'pointer' }}
                      >
                        {isBusy ? '…' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Compliance Audits ── */}
      <div style={{ marginTop: '40px' }}>
        <div style={{ fontSize: '12px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#3b82f6', marginBottom: '12px' }}>
          Compliance Audits — AI Security Doctrine
        </div>

        {/* Static reference card */}
        <div style={{ background: '#080f1a', border: '1px solid rgba(59,130,246,0.18)', borderRadius: '8px', padding: '16px 20px', marginBottom: '16px', fontSize: '13px', color: '#4a6a80', lineHeight: 1.7 }}>
          <div style={{ color: '#60a5fa', fontWeight: 600, marginBottom: '6px' }}>Doctrine v1.0 — March 1, 2026</div>
          <div>Governing statement: <em style={{ color: '#8aaec8' }}>"All security enforcement mechanisms derive from the AI Security Doctrine, Version 1.0."</em></div>
          <div style={{ marginTop: '8px', display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            <span>Public page: <a href="/security-doctrine" target="_blank" rel="noreferrer" style={{ color: '#3b82f6' }}>/security-doctrine</a></span>
            <span>Run test: <code style={{ background: '#0d1a28', padding: '1px 7px', borderRadius: '4px', color: '#93c5fd' }}>node scripts/doctrineComplianceTest.js</code></span>
            <span>Reports: <code style={{ background: '#0d1a28', padding: '1px 7px', borderRadius: '4px', color: '#93c5fd' }}>docs/compliance/</code></span>
          </div>
        </div>

        {auditsLoading && <div style={{ color: '#3a4560', fontSize: '13px' }}>Loading audit records…</div>}
        {!auditsLoading && audits.length === 0 && (
          <div style={{ color: '#3a4560', fontSize: '13px' }}>
            No Firestore audit records yet. Run <code style={{ color: '#60a5fa' }}>node scripts/doctrineComplianceTest.js --push</code> to write the first one.
          </div>
        )}
        {audits.length > 0 && (
          <div style={{ overflowX: 'auto', border: '1px solid #1e2a3a', borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
              <thead>
                <tr>
                  <th style={thS}>Run Date</th>
                  <th style={thS}>Doctrine</th>
                  <th style={thS}>Verdict</th>
                  <th style={thS}>Pass</th>
                  <th style={thS}>Fail</th>
                  <th style={thS}>GAP</th>
                  <th style={thS}>Patterns</th>
                </tr>
              </thead>
              <tbody>
                {audits.map(a => {
                  const ts = a.runAt ? (a.runAt.toDate ? a.runAt.toDate() : new Date(a.runAt)) : null;
                  const dateStr = ts ? ts.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : (a.runAtIso || '—');
                  const verdict = a.verdict || (a.summary?.fail === 0 ? 'PASS' : 'FAIL');
                  return (
                    <tr key={a.id}>
                      <td style={{ ...tdS, whiteSpace: 'nowrap', color: '#8aaec8', fontSize: '12px' }}>{dateStr}</td>
                      <td style={{ ...tdS, color: '#60a5fa', fontSize: '12px' }}>v{a.doctrineVersion}</td>
                      <td style={tdS}>
                        <span style={{ padding: '2px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: 700,
                          background: verdict === 'PASS' ? 'rgba(52,211,153,0.12)' : 'rgba(248,113,113,0.12)',
                          color: verdict === 'PASS' ? '#34d399' : '#f87171' }}>
                          {verdict}
                        </span>
                      </td>
                      <td style={{ ...tdS, color: '#34d399', fontWeight: 600 }}>{a.summary?.pass ?? '—'}</td>
                      <td style={{ ...tdS, color: a.summary?.fail > 0 ? '#f87171' : '#3a4560' }}>{a.summary?.fail ?? '—'}</td>
                      <td style={{ ...tdS, color: '#6b8fa8' }}>{a.summary?.gap ?? '—'}</td>
                      <td style={{ ...tdS, color: '#4a6a80' }}>{a.patternCount ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Student Intelligence Panel ────────────────────────────────────────────────
function riskMeta(risk) {
  if (risk >= 65) return { color: '#f87171', bg: '#2a0d0d', label: 'HIGH', dot: '●' };
  if (risk >= 35) return { color: '#fbbf24', bg: '#2a1e0d', label: 'MED',  dot: '●' };
  return               { color: '#34d399', bg: '#0d2a1e', label: 'LOW',  dot: '●' };
}
function engMeta(score) {
  if (score >= 70) return { color: '#34d399', label: 'Strong' };
  if (score >= 40) return { color: '#fbbf24', label: 'Moderate' };
  return               { color: '#f87171', label: 'Low' };
}
const SIGNAL_LABELS = {
  session_gap_widening:              { label: 'Gap ↑',        color: '#fbbf24' },
  response_length_declining:         { label: 'Resp ↓',       color: '#f87171' },
  repeated_sessions_without_progress:{ label: 'No Progress',  color: '#f87171' },
  low_engagement_sustained:          { label: 'Low Eng',      color: '#fb923c' },
  abandoned_session:                 { label: 'Abandoned',    color: '#f87171' },
  lesson_stall:                      { label: 'Stalled',      color: '#f97316' },
  post_checkpoint_drop:              { label: 'Post-Chkpt ↓', color: '#fbbf24' },
  slow_start:                        { label: 'Slow Start',   color: '#94a3b8' },
};

function fatigueMeta(score) {
  if (score >= 75) return { color: '#f97316', bg: '#2a1500', label: 'HIGH' };
  if (score >= 50) return { color: '#fbbf24', bg: '#2a1e0d', label: 'MED'  };
  return               { color: '#4a5568',  bg: 'transparent', label: '' };
}

function StudentIntelligencePanel() {
  const [students, setStudents] = useState([]);
  const [sortKey, setSortKey] = useState('dropoutRisk');
  const [filterMode, setFilterMode] = useState('all'); // all | atrisk | intervention

  useEffect(() => {
    // Listen to all tracked students — beta testers + paying tier users
    const q = query(
      collection(db, 'users'),
      where('isBeta', '==', true)
    );
    const unsub = onSnapshot(q, snap => {
      const rows = snap.docs
        .map(d => ({ uid: d.id, ...d.data() }))
        .filter(u => u.learnerProfile); // only users who have session data
      setStudents(rows);
    }, () => {});
    return unsub;
  }, []);

  const filtered = students.filter(u => {
    const lp = u.learnerProfile || {};
    if (filterMode === 'atrisk')       return (lp.dropoutRisk || 0) >= 35;
    if (filterMode === 'intervention') return !!lp.interventionNeeded;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    const la = a.learnerProfile || {};
    const lb = b.learnerProfile || {};
    if (sortKey === 'dropoutRisk')    return (lb.dropoutRisk || 0) - (la.dropoutRisk || 0);
    if (sortKey === 'fatigue')         return (lb.fatigueScore || 0) - (la.fatigueScore || 0);
    if (sortKey === 'engagement')     return (lb.engagementScore || 0) - (la.engagementScore || 0);
    if (sortKey === 'totalSessions')  return (lb.totalSessions || 0) - (la.totalSessions || 0);
    if (sortKey === 'lastSessionAt') {
      const ta = la.lastSessionAt?.toMillis?.() || 0;
      const tb = lb.lastSessionAt?.toMillis?.() || 0;
      return tb - ta;
    }
    return 0;
  });

  const interventionCount = students.filter(u => u.learnerProfile?.interventionNeeded).length;
  const atRiskCount       = students.filter(u => (u.learnerProfile?.dropoutRisk || 0) >= 35).length;

  const thS = {
    padding: '8px 12px', color: '#4f8ef7', fontSize: '11px',
    fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
    textAlign: 'left', whiteSpace: 'nowrap', borderBottom: '2px solid #1e2a3a',
    background: '#060b14', position: 'sticky', top: 0, cursor: 'pointer',
  };
  const tdS = {
    padding: '9px 12px', fontSize: '13px', color: '#c4cde0',
    borderBottom: '1px solid #0e1828', verticalAlign: 'middle',
  };

  function SortTh({ label, sk }) {
    return (
      <th style={{ ...thS, color: sortKey === sk ? '#f0f4ff' : '#4f8ef7' }}
          onClick={() => setSortKey(sk)}>
        {label}{sortKey === sk ? ' ↓' : ''}
      </th>
    );
  }

  return (
    <div style={{ padding: '0 40px 40px' }}>

      {/* Summary bar */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {[
          { label: 'Tracked',     val: students.length,   color: '#4f8ef7' },
          { label: 'At Risk',     val: atRiskCount,        color: '#fbbf24' },
          { label: 'Intervention Needed', val: interventionCount, color: '#f87171' },
        ].map(({ label, val, color }) => (
          <div key={label} style={{ background: '#0d1220', border: `1px solid ${color}33`,
            borderRadius: '10px', padding: '12px 20px', textAlign: 'center', minWidth: '90px' }}>
            <div style={{ fontSize: '22px', fontWeight: 700, color }}>{val}</div>
            <div style={{ fontSize: '11px', color: '#4a5568', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</div>
          </div>
        ))}

        {/* Filter buttons */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginLeft: 'auto' }}>
          {[['all','All'],['atrisk','At Risk'],['intervention','Intervention']].map(([k,l]) => (
            <button key={k} onClick={() => setFilterMode(k)}
              style={{ background: filterMode === k ? '#1e2a3a' : 'none',
                border: `1px solid ${filterMode === k ? '#4f8ef7' : '#1e2a3a'}`,
                borderRadius: '6px', color: filterMode === k ? '#f0f4ff' : '#4a5568',
                fontSize: '12px', padding: '5px 14px', cursor: 'pointer' }}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {students.length === 0 && (
        <div style={{ color: '#3a4560', fontSize: '14px' }}>No student session data yet. Data appears after the first chat session.</div>
      )}

      {sorted.length === 0 && students.length > 0 && (
        <div style={{ color: '#3a4560', fontSize: '14px' }}>No students match this filter.</div>
      )}

      {sorted.length > 0 && (
        <div style={{ overflowX: 'auto', border: '1px solid #1e2a3a', borderRadius: '8px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}>
            <thead>
              <tr>
                <th style={thS}>Student</th>
                <th style={thS}>Tier</th>
                <SortTh label="Sessions" sk="totalSessions" />
                <SortTh label="Engagement" sk="engagement" />
                <SortTh label="Fatigue" sk="fatigue" />
                <SortTh label="Dropout Risk" sk="dropoutRisk" />
                <th style={thS}>Signals</th>
                <th style={thS}>Level</th>
                <SortTh label="Last Session" sk="lastSessionAt" />
                <th style={thS}>⚠</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((u, i) => {
                const lp   = u.learnerProfile || {};
                const risk    = lp.dropoutRisk || 0;
                const eng     = lp.engagementScore || 0;
                const fatigue = lp.fatigueScore || 0;
                const rm  = riskMeta(risk);
                const em  = engMeta(eng);
                const fm  = fatigueMeta(fatigue);
                const signals = Array.isArray(lp.riskSignals) ? lp.riskSignals : [];
                const rowBg = i % 2 === 0 ? '#060b14' : '#08101c';
                return (
                  <tr key={u.uid} style={{ backgroundColor: rowBg }}>
                    <td style={tdS}>
                      <div style={{ color: '#f0f4ff', fontWeight: 600 }}>{u.name || '—'}</div>
                      <div style={{ fontSize: '11px', color: '#4a5568' }}>{u.email}</div>
                    </td>
                    <td style={tdS}>
                      <span style={{
                        fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '999px',
                        border: `1px solid ${(TIER_COLORS[u.tier] || TIER_COLORS.foundations).text}55`,
                        backgroundColor: (TIER_COLORS[u.tier] || TIER_COLORS.foundations).bg,
                        color: (TIER_COLORS[u.tier] || TIER_COLORS.foundations).text,
                      }}>
                        {TIER_COLORS[u.tier]?.label || u.tier || '—'}
                      </span>
                    </td>
                    <td style={{ ...tdS, textAlign: 'center', fontFamily: 'monospace',
                      color: (lp.totalSessions || 0) > 0 ? '#4f8ef7' : '#3a4560', fontWeight: 600 }}>
                      {lp.totalSessions || 0}
                    </td>
                    <td style={tdS}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{ width: '60px', height: '6px', borderRadius: '3px',
                          background: '#1e2a3a', overflow: 'hidden' }}>
                          <div style={{ width: `${eng}%`, height: '100%',
                            background: em.color, borderRadius: '3px', transition: 'width 0.3s' }} />
                        </div>
                        <span style={{ fontSize: '12px', color: em.color, fontWeight: 600 }}>{eng}</span>
                      </div>
                    </td>
                    <td style={tdS}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{ width: '60px', height: '6px', borderRadius: '3px',
                          background: '#1e2a3a', overflow: 'hidden' }}>
                          <div style={{ width: `${fatigue}%`, height: '100%',
                            background: fm.color, borderRadius: '3px', transition: 'width 0.3s' }} />
                        </div>
                        <span style={{ fontSize: '12px', color: fm.color, fontWeight: 600 }}>{fatigue || '—'}</span>
                        {fm.label && (
                          <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px',
                            background: fm.bg, color: fm.color, fontWeight: 700 }}>{fm.label}</span>
                        )}
                        {lp.fatigueCheckInNeeded && (
                          <span title="Check-in needed" style={{ fontSize: '13px' }}>😓</span>
                        )}
                      </div>
                    </td>
                    <td style={tdS}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{ width: '60px', height: '6px', borderRadius: '3px',
                          background: '#1e2a3a', overflow: 'hidden' }}>
                          <div style={{ width: `${risk}%`, height: '100%',
                            background: rm.color, borderRadius: '3px', transition: 'width 0.3s' }} />
                        </div>
                        <span style={{ fontSize: '12px', color: rm.color, fontWeight: 700 }}>{risk}</span>
                        <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px',
                          background: rm.bg, color: rm.color, fontWeight: 700 }}>{rm.label}</span>
                      </div>
                    </td>
                    <td style={tdS}>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {signals.length === 0
                          ? <span style={{ color: '#2a3550', fontSize: '11px' }}>—</span>
                          : signals.map(s => {
                              const meta = SIGNAL_LABELS[s] || { label: s, color: '#4a5568' };
                              return (
                                <span key={s} style={{ fontSize: '10px', padding: '2px 7px',
                                  borderRadius: '4px', background: meta.color + '22',
                                  color: meta.color, fontWeight: 600, border: `1px solid ${meta.color}44`,
                                  whiteSpace: 'nowrap' }}>
                                  {meta.label}
                                </span>
                              );
                            })
                        }
                      </div>
                    </td>
                    <td style={{ ...tdS, fontSize: '11px', color: lp.currentLevel ? '#a78bfa' : '#3a4560',
                      fontWeight: lp.currentLevel ? 600 : 400 }}>
                      {lp.currentLevel || '—'}
                    </td>
                    <td style={{ ...tdS, fontSize: '12px', color: '#4a5568', whiteSpace: 'nowrap' }}>
                      {lp.lastSessionAt ? (() => {
                        const ms   = lp.lastSessionAt?.toMillis?.() || new Date(lp.lastSessionAt).getTime();
                        const days = Math.round((Date.now() - ms) / (1000 * 60 * 60 * 24));
                        return (
                          <>
                            {fmtDateTime(lp.lastSessionAt)}
                            {days >= 2 && (
                              <span title={`${days} days since last session`} style={{
                                marginLeft: 6, background: days >= 7 ? '#ef4444' : '#f97316',
                                color: '#fff', borderRadius: 4, fontSize: 10,
                                padding: '2px 5px', fontWeight: 700,
                              }}>{days}d away</span>
                            )}
                          </>
                        );
                      })() : '—'}
                    </td>
                    <td style={{ ...tdS, textAlign: 'center' }}>
                      {lp.interventionNeeded
                        ? <span title="Intervention needed" style={{ fontSize: '16px' }}>🚨</span>
                        : <span style={{ color: '#2a3550' }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
function ExperienceControlsPanel() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [cfg, setCfg] = useState({
    defaultAudioEnabled: true,
    defaultVoice: 'onyx',
    processingTickMs: 45,
    processingLabel: 'processing...',
    loadingLabel: 'loading...',
  });

  useEffect(() => {
    let cancelled = false;
    async function loadCfg() {
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, 'admin_controls', 'chatExperience'));
        if (!snap.exists() || cancelled) {
          setLoading(false);
          return;
        }
        const data = snap.data() || {};
        if (!cancelled) {
          setCfg(prev => ({
            ...prev,
            defaultAudioEnabled: typeof data.defaultAudioEnabled === 'boolean' ? data.defaultAudioEnabled : prev.defaultAudioEnabled,
            defaultVoice: typeof data.defaultVoice === 'string' && data.defaultVoice.trim() ? data.defaultVoice.trim() : prev.defaultVoice,
            processingTickMs: Number.isFinite(Number(data.processingTickMs)) ? Math.max(20, Math.min(160, Math.floor(Number(data.processingTickMs)))) : prev.processingTickMs,
            processingLabel: typeof data.processingLabel === 'string' && data.processingLabel.trim() ? data.processingLabel.trim() : prev.processingLabel,
            loadingLabel: typeof data.loadingLabel === 'string' && data.loadingLabel.trim() ? data.loadingLabel.trim() : prev.loadingLabel,
          }));
        }
      } catch {
        if (!cancelled) setStatus('Unable to load chat experience config.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadCfg();
    return () => { cancelled = true; };
  }, []);

  async function saveCfg() {
    setSaving(true);
    setStatus('');
    try {
      const payload = {
        defaultAudioEnabled: !!cfg.defaultAudioEnabled,
        defaultVoice: String(cfg.defaultVoice || 'onyx').trim() || 'onyx',
        processingTickMs: Math.max(20, Math.min(160, Number(cfg.processingTickMs) || 45)),
        processingLabel: String(cfg.processingLabel || 'processing...').trim() || 'processing...',
        loadingLabel: String(cfg.loadingLabel || 'loading...').trim() || 'loading...',
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.email || 'admin',
      };
      await setDoc(doc(db, 'admin_controls', 'chatExperience'), payload, { merge: true });
      setStatus('Saved. New chat sessions pick this up automatically.');
    } catch (err) {
      setStatus(err?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      background: '#0f1829', border: '1px solid #1e2a3a', borderRadius: 14,
      padding: 20, maxWidth: 860,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#f0f4ff' }}>Experience Controls</div>
          <div style={{ fontSize: 12, color: '#7b8aa6', marginTop: 4 }}>
            Global defaults for Instructor Chat audio and processing UI.
          </div>
        </div>
        <button
          onClick={saveCfg}
          disabled={saving || loading}
          style={{
            padding: '8px 14px', borderRadius: 8, border: '1px solid #2a3a50',
            background: saving ? '#182235' : '#1f2d44', color: '#d8e5ff', cursor: saving ? 'wait' : 'pointer',
            fontSize: 12, fontWeight: 700,
          }}
        >
          {saving ? 'Saving...' : 'Save Controls'}
        </button>
      </div>

      {loading ? (
        <div style={{ fontSize: 13, color: '#7b8aa6' }}>Loading controls...</div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#c9d8f6' }}>
              <input
                type="checkbox"
                checked={cfg.defaultAudioEnabled}
                onChange={e => setCfg(p => ({ ...p, defaultAudioEnabled: e.target.checked }))}
              />
              Default audio ON for new users
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: '#8ea2c8' }}>
              Default voice
              <select
                value={cfg.defaultVoice}
                onChange={e => setCfg(p => ({ ...p, defaultVoice: e.target.value }))}
                style={{ background: '#0b1220', color: '#d8e5ff', border: '1px solid #22324a', borderRadius: 8, padding: '8px 10px' }}
              >
                <option value="onyx">Jarvis-like (Onyx)</option>
                <option value="nova">Pepper-like (Nova)</option>
                <option value="alloy">Alloy</option>
                <option value="echo">Echo</option>
                <option value="fable">Fable</option>
                <option value="shimmer">Shimmer</option>
              </select>
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: '#8ea2c8' }}>
              Processing stream speed (ms)
              <input
                type="number"
                min={20}
                max={160}
                value={cfg.processingTickMs}
                onChange={e => setCfg(p => ({ ...p, processingTickMs: Number(e.target.value || 45) }))}
                style={{ background: '#0b1220', color: '#d8e5ff', border: '1px solid #22324a', borderRadius: 8, padding: '8px 10px' }}
              />
            </label>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14, marginTop: 14 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: '#8ea2c8' }}>
              Processing label
              <input
                type="text"
                value={cfg.processingLabel}
                onChange={e => setCfg(p => ({ ...p, processingLabel: e.target.value }))}
                style={{ background: '#0b1220', color: '#d8e5ff', border: '1px solid #22324a', borderRadius: 8, padding: '8px 10px' }}
              />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: '#8ea2c8' }}>
              Loading label
              <input
                type="text"
                value={cfg.loadingLabel}
                onChange={e => setCfg(p => ({ ...p, loadingLabel: e.target.value }))}
                style={{ background: '#0b1220', color: '#d8e5ff', border: '1px solid #22324a', borderRadius: 8, padding: '8px 10px' }}
              />
            </label>
          </div>

          <div style={{ marginTop: 14, fontSize: 12, color: '#7b8aa6' }}>
            Pattern reused from FreedomCross artwork controls: centralized tweak surface + persistent config + immediate admin testing loop.
          </div>
          {status && <div style={{ marginTop: 10, fontSize: 12, color: status.startsWith('Saved') ? '#34d399' : '#f87171' }}>{status}</div>}
        </>
      )}
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function AdminDashboard() {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('requests');
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [actionState, setActionState] = useState({});
  const [counts, setCounts] = useState({ pending: 0, approved: 0, denied: 0 });
  const [feedbackCount, setFeedbackCount] = useState(0);
  const [flaggedCount, setFlaggedCount] = useState(0);
  const [securityCount, setSecurityCount] = useState(0);
  const [activeUsers, setActiveUsers] = useState([]);

  const [navOpen, setNavOpen] = useState(false);
  const navRef = useRef(null);

  // Close nav dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (navRef.current && !navRef.current.contains(e.target)) setNavOpen(false);
    }
    if (navOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [navOpen]);

  const isAdmin = isAdminEmail(currentUser?.email);

  // Live listener on access_requests
  useEffect(() => {
    if (!isAdmin) return;
    const q = filter === 'all'
      ? query(collection(db, 'access_requests'), orderBy('requestedAt', 'desc'))
      : query(
          collection(db, 'access_requests'),
          where('status', '==', filter),
          orderBy('requestedAt', 'desc')
        );
    const unsub = onSnapshot(q, (snap) => {
      setRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [filter, isAdmin]);

  // Live counts
  useEffect(() => {
    if (!isAdmin) return;
    const statuses = ['pending', 'approved', 'denied'];
    const unsubs = statuses.map(status => {
      const q = query(collection(db, 'access_requests'), where('status', '==', status));
      return onSnapshot(q, snap => {
        setCounts(prev => ({ ...prev, [status]: snap.size }));
      });
    });
    return () => unsubs.forEach(u => u());
  }, [isAdmin]);

  // Feedback count badge — unacknowledged only
  useEffect(() => {
    if (!isAdmin) return;
    const unsub = onSnapshot(collection(db, 'feedback'), snap => {
      const unacked = snap.docs.filter(d => !d.data().acknowledged).length;
      setFeedbackCount(unacked);
    }, err => console.warn('[feedbackCount]', err.code));
    return unsub;
  }, [isAdmin]);

  // Active users — last seen within 10 minutes
  useEffect(() => {
    if (!isAdmin) return;
    const tenMinAgo = new Date(Date.now() - 10 * 60 * 1000);
    const q = query(collection(db, 'users'), where('lastSeen', '>=', tenMinAgo));
    const unsub = onSnapshot(q, snap => {
      const users = snap.docs.map(d => ({ uid: d.id, ...d.data() }));
      users.sort((a, b) => (b.lastSeen?.toMillis?.() || 0) - (a.lastSeen?.toMillis?.() || 0));
      setActiveUsers(users);
    }, () => {});
    return unsub;
  }, [isAdmin]);

  // Flagged accounts count badge
  useEffect(() => {
    if (!isAdmin) return;
    const q = query(collection(db, 'users'), where('flagged', '==', true));
    const unsub = onSnapshot(q, snap => setFlaggedCount(snap.size), () => {});
    return unsub;
  }, [isAdmin]);

  // Security events count badge — unacknowledged only
  useEffect(() => {
    if (!isAdmin) return;
    const unsub = onSnapshot(collection(db, 'security_logs'), snap => {
      const unacked = snap.docs.filter(d => !d.data().acknowledged).length;
      setSecurityCount(unacked);
    }, () => {});
    return unsub;
  }, [isAdmin]);

  async function handleApprove(req) {
    setActionState(prev => ({ ...prev, [req.id]: 'approving' }));
    try {
      const fns = getFunctions();
      const approveStudent = httpsCallable(fns, 'approveStudent');
      await approveStudent({ requestId: req.id, email: req.email, name: req.name });
      setActionState(prev => ({ ...prev, [req.id]: 'done' }));
    } catch (err) {
      setActionState(prev => ({ ...prev, [req.id]: 'error:' + err.message }));
    }
  }

  async function handleDeny(req) {
    setActionState(prev => ({ ...prev, [req.id]: 'denying' }));
    try {
      const fns = getFunctions();
      const denyStudent = httpsCallable(fns, 'denyStudent');
      await denyStudent({ requestId: req.id });
      setActionState(prev => ({ ...prev, [req.id]: 'done' }));
    } catch (err) {
      setActionState(prev => ({ ...prev, [req.id]: 'error:' + err.message }));
    }
  }

  function formatDate(ts) {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  if (!isAdmin) {
    return (
      <div style={styles.page}>
        <div style={{ padding: '60px 40px' }}>
          <p style={{ color: '#f87171' }}>Access denied.</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { key: 'requests', label: 'Access Requests', badge: counts.pending || null, badgeColor: '#f59e0b' },
    { key: 'invites', label: 'Beta Invites', badge: null },
    { key: 'beta', label: 'Beta Access', badge: null },
    { key: 'feedback', label: 'Feedback', badge: feedbackCount || null, badgeColor: '#4f8ef7' },
    { key: 'flagged', label: '🚩 Flagged', badge: flaggedCount || null, badgeColor: '#f87171' },
    { key: 'security', label: '🔒 Security', badge: securityCount || null, badgeColor: '#f87171' },
    { key: 'experience', label: '🎛 Experience', badge: null },
    { key: 'intelligence', label: '🧠 Student Intel', badge: null },
    { key: 'promptlab',    label: '🧪 Prompt Lab',    badge: null },
  ];

  return (
    <div style={styles.page}>
      {/* Top bar */}
      <div style={styles.topBar}>
        <div>
          <div style={styles.brand}>THE OPERATOR METHOD</div>
          <div style={styles.pageTitle}>Admin Dashboard</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: activeUsers.length > 0 ? "#34d399" : "#334155", display: "inline-block", flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: "#64748b" }}>
            {activeUsers.length > 0 ? `${activeUsers.length} active now` : "No active users"}
          </span>
          {activeUsers.length > 0 && (
            <span style={{ fontSize: 11, color: "#475569" }}>
              ({activeUsers.map(u => u.email || u.uid.slice(0, 8)).join(", ")})
            </span>
          )}
        </div>
        <button onClick={async () => { sessionStorage.setItem('om_intentional_signout', '1'); await signOut(auth); window.location.href = '/login'; }} style={styles.signOutBtn}>Sign Out</button>
        {/* Navigate To dropdown */}
        <div ref={navRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setNavOpen(o => !o)}
            style={{
              padding: '7px 14px',
              backgroundColor: navOpen ? '#1e2a3a' : '#0f1829',
              border: '1px solid #1e2a3a',
              borderRadius: '6px',
              color: '#94a3b8',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              letterSpacing: '0.04em',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            Navigate To {navOpen ? '▲' : '▼'}
          </button>
          {navOpen && (
            <div style={{
              position: 'absolute',
              right: 0,
              top: 'calc(100% + 6px)',
              backgroundColor: '#0a0f1e',
              border: '1px solid #1e2a3a',
              borderRadius: '10px',
              padding: '8px 0',
              minWidth: '260px',
              maxHeight: '70vh',
              overflowY: 'auto',
              zIndex: 200,
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            }}>
              {[
                { label: 'KEY PAGES', type: 'header' },
                { label: 'V2 Course', url: '/v2-course' },
                { label: 'Pricing Page', url: '/pricing' },
                { label: 'User Dashboard', url: '/dashboard' },
                { label: 'Module 0 — The Crew', type: 'header' },
                { label: 'Lesson 0: Meet Your Crew', url: '/course/module-0/lesson-0' },
                { label: 'Lesson 1: Economy of Attention', url: '/course/module-0/lesson-1' },
                { label: 'Lesson 2: Plan Before You Type', url: '/course/module-0/lesson-2' },
                { label: 'Lesson 3: Know When to Stop', url: '/course/module-0/lesson-3' },
                { label: 'Module 1 — The Brief', type: 'header' },
                { label: 'Lesson 0: Why Context Is the Whole Job', url: '/course/module-1/lesson-0' },
                { label: 'Lesson 1: The Five-Part Brief', url: '/course/module-1/lesson-1' },
                { label: 'Lesson 2: Background Loading', url: '/course/module-1/lesson-2' },
                { label: 'Lesson 3: Clear Objective', url: '/course/module-1/lesson-3' },
                { label: 'Lesson 4: The Blindfold Drill', url: '/course/module-1/lesson-4' },
                { label: 'Module 2 — The Directive', type: 'header' },
                { label: 'Lesson 0: Read Critically', url: '/course/module-2/lesson-0' },
                { label: 'Lesson 1: Steering Vocabulary', url: '/course/module-2/lesson-1' },
                { label: 'Lesson 2: Keeping Your Thread', url: '/course/module-2/lesson-2' },
                { label: 'Lesson 3: Confidently Wrong', url: '/course/module-2/lesson-3' },
                { label: 'Lesson 4: Restart Clean', url: '/course/module-2/lesson-4' },
                { label: 'Module 3 — The Stack', type: 'header' },
                { label: 'Lesson 0: Your Tool Belt', url: '/course/module-3/lesson-0' },
                { label: 'Lesson 1: Chaining Prompts', url: '/course/module-3/lesson-1' },
                { label: 'Lesson 2: Templates & Playbooks', url: '/course/module-3/lesson-2' },
                { label: 'Lesson 3: When to Automate', url: '/course/module-3/lesson-3' },
                { label: 'Lesson 4: The Feedback Loop', url: '/course/module-3/lesson-4' },
                { label: 'Lesson 5: Stack Audit', url: '/course/module-3/lesson-5' },
                { label: 'Module 4 — The Review', type: 'header' },
                { label: 'Lesson 0: Field Test', url: '/course/module-4/lesson-0' },
                { label: 'Lesson 1: Operator Patterns', url: '/course/module-4/lesson-1' },
                { label: 'Lesson 2: Edge Cases', url: '/course/module-4/lesson-2' },
                { label: 'Lesson 3: Teaching Others', url: '/course/module-4/lesson-3' },
                { label: 'Lesson 4: Certification', url: '/course/module-4/lesson-4' },
              ].map((item, i) => item.type === 'header' ? (
                <div key={i} style={{ padding: '8px 14px 4px', fontSize: '9px', fontWeight: 700, letterSpacing: '0.15em', color: '#4f8ef7', textTransform: 'uppercase' }}>
                  {item.label}
                </div>
              ) : (
                <button
                  key={i}
                  onClick={() => { window.open(item.url, '_blank'); setNavOpen(false); }}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '7px 14px',
                    background: 'none',
                    border: 'none',
                    color: '#c4cde0',
                    fontSize: '12px',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#0f1829'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tab nav */}
      <div style={styles.tabNav}>
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              ...styles.tabBtn,
              borderBottom: activeTab === tab.key ? '2px solid #4f8ef7' : '2px solid transparent',
              color: activeTab === tab.key ? '#f0f4ff' : '#6b7a99',
            }}
          >
            {tab.label}
            {tab.badge != null && (
              <span style={{ ...styles.tabBadge, backgroundColor: tab.badgeColor || '#4f8ef7' }}>
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Requests tab ── */}
      {activeTab === 'requests' && (
        <>
          <div style={styles.statsRow}>
            {[
              { label: 'Pending', key: 'pending', color: '#f59e0b' },
              { label: 'Approved', key: 'approved', color: '#34d399' },
              { label: 'Denied', key: 'denied', color: '#f87171' },
            ].map(({ label, key, color }) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                style={{
                  ...styles.statChip,
                  borderColor: filter === key ? color : '#1e2a3a',
                  color: filter === key ? color : '#6b7a99',
                }}
              >
                <span style={{ fontSize: '22px', fontWeight: 700, color }}>{counts[key]}</span>
                <span style={{ fontSize: '12px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</span>
              </button>
            ))}
            <button
              onClick={() => setFilter('all')}
              style={{
                ...styles.statChip,
                borderColor: filter === 'all' ? '#4f8ef7' : '#1e2a3a',
                color: filter === 'all' ? '#4f8ef7' : '#6b7a99',
              }}
            >
              <span style={{ fontSize: '22px', fontWeight: 700 }}>All</span>
              <span style={{ fontSize: '12px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>View All</span>
            </button>
          </div>

          <div style={styles.list}>
            {loading && <div style={styles.empty}>Loading...</div>}
            {!loading && requests.length === 0 && (
              <div style={styles.empty}>No {filter} requests.</div>
            )}
            {requests.map(req => {
              const state = actionState[req.id];
              const isBusy = state === 'approving' || state === 'denying';
              const isDone = state === 'done';
              const isError = state?.startsWith('error:');

              return (
                <div key={req.id} style={{ ...styles.requestCard, opacity: isDone ? 0.5 : 1 }}>
                  <div style={styles.requestLeft}>
                    <div style={styles.requestName}>{req.name || <span style={{ color: '#3a4560' }}>No name provided</span>}</div>
                    <div style={styles.requestEmail}>{req.email}</div>
                    {req.background && (
                      <div style={styles.requestBackground}>"{req.background}"</div>
                    )}
                    <div style={styles.requestDate}>{formatDate(req.requestedAt)}</div>
                  </div>
                  <div style={styles.requestRight}>
                    {isDone && <div style={{ color: '#34d399', fontSize: '14px' }}>✓ Done</div>}
                    {isError && <div style={{ color: '#f87171', fontSize: '12px', maxWidth: '160px' }}>{state.replace('error:', '')}</div>}
                    {!isDone && req.status === 'pending' && (
                      <>
                        <button onClick={() => handleApprove(req)} disabled={isBusy} style={{ ...styles.approveBtn, opacity: isBusy ? 0.5 : 1 }}>
                          {state === 'approving' ? 'Approving...' : 'Approve'}
                        </button>
                        <button onClick={() => handleDeny(req)} disabled={isBusy} style={{ ...styles.denyBtn, opacity: isBusy ? 0.5 : 1 }}>
                          {state === 'denying' ? 'Denying...' : 'Deny'}
                        </button>
                      </>
                    )}
                    {req.status !== 'pending' && (
                      <div style={{ fontSize: '12px', letterSpacing: '0.1em', textTransform: 'uppercase', color: req.status === 'approved' ? '#34d399' : '#f87171' }}>
                        {req.status}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── Beta Invites tab ── */}
      {activeTab === 'invites' && <BetaInvitesPanel />}

      {/* ── Beta Access tab ── */}
      {activeTab === 'beta' && <BetaPanel />}

      {/* ── Feedback tab ── */}
      {activeTab === 'feedback' && <FeedbackPanel />}

      {/* ── Flagged Accounts tab ── */}
      {activeTab === 'flagged' && <FlaggedPanel />}

      {/* ── Security Events tab ── */}
      {activeTab === 'security' && <SecurityPanel />}

      {/* ── Experience Controls tab ── */}
      {activeTab === 'experience' && <ExperienceControlsPanel />}

      {/* ── Student Intelligence tab ── */}
      {activeTab === 'intelligence' && <StudentIntelligencePanel />}

      {/* ── Prompt Lab tab ── */}
      {activeTab === 'promptlab' && <AdminPromptLab />}
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#060b14',
    fontFamily: "'Inter', -apple-system, sans-serif",
    color: '#f0f4ff',
    padding: '0',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '24px 40px',
    borderBottom: '1px solid #0e1828',
    backgroundColor: '#080d18',
  },
  brand: {
    fontSize: '10px',
    letterSpacing: '0.3em',
    color: '#4f8ef7',
    fontWeight: 600,
    marginBottom: '4px',
  },
  pageTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#f0f4ff',
  },
  signOutBtn: {
    background: 'none',
    border: '1px solid #1e2a3a',
    color: '#6b7a99',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
  },
  tabNav: {
    display: 'flex',
    gap: '0',
    padding: '0 40px',
    borderBottom: '1px solid #0e1828',
    backgroundColor: '#080d18',
  },
  tabBtn: {
    background: 'none',
    border: 'none',
    borderBottom: '2px solid transparent',
    color: '#6b7a99',
    padding: '14px 20px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'color 0.15s',
  },
  tabBadge: {
    fontSize: '11px',
    fontWeight: 700,
    padding: '2px 7px',
    borderRadius: '20px',
    color: '#060b14',
  },
  statsRow: {
    display: 'flex',
    gap: '12px',
    padding: '24px 40px',
    flexWrap: 'wrap',
  },
  statChip: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    padding: '14px 24px',
    backgroundColor: '#0d1220',
    border: '1px solid',
    borderRadius: '10px',
    cursor: 'pointer',
    minWidth: '100px',
    transition: 'all 0.15s',
  },
  list: {
    padding: '0 40px 40px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    maxWidth: '900px',
  },
  empty: {
    color: '#3a4560',
    fontSize: '15px',
    padding: '40px 0',
  },
  requestCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#0d1220',
    border: '1px solid #1e2a3a',
    borderRadius: '12px',
    padding: '20px 24px',
    gap: '20px',
    transition: 'opacity 0.3s',
  },
  requestLeft: {
    flex: 1,
    minWidth: 0,
  },
  requestName: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#f0f4ff',
    marginBottom: '4px',
  },
  requestEmail: {
    fontSize: '14px',
    color: '#4f8ef7',
    marginBottom: '8px',
  },
  requestBackground: {
    fontSize: '13px',
    color: '#6b7a99',
    fontStyle: 'italic',
    marginBottom: '8px',
    maxWidth: '500px',
  },
  requestDate: {
    fontSize: '12px',
    color: '#3a4560',
  },
  requestRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '8px',
    flexShrink: 0,
  },
  approveBtn: {
    padding: '9px 20px',
    backgroundColor: '#0f2b1a',
    border: '1px solid #1a5c30',
    color: '#34d399',
    borderRadius: '7px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 600,
    transition: 'opacity 0.15s',
  },
  denyBtn: {
    padding: '9px 20px',
    backgroundColor: '#1f0f0f',
    border: '1px solid #5c1f1f',
    color: '#f87171',
    borderRadius: '7px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 600,
    transition: 'opacity 0.15s',
  },
  panelCard: {
    backgroundColor: '#0d1220',
    border: '1px solid #1e2a3a',
    borderRadius: '12px',
    padding: '28px 32px',
    marginTop: '28px',
  },
  panelHeading: {
    fontSize: '17px',
    fontWeight: 600,
    color: '#f0f4ff',
    marginBottom: '8px',
  },
  panelSub: {
    fontSize: '13px',
    color: '#6b7a99',
    marginBottom: '24px',
    lineHeight: 1.6,
  },
  fieldLabel: {
    display: 'block',
    fontSize: '12px',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#6b7a99',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    backgroundColor: '#060b14',
    border: '1px solid #1e2a3a',
    borderRadius: '7px',
    color: '#f0f4ff',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  feedbackCard: {
    backgroundColor: '#0d1220',
    border: '1px solid #1e2a3a',
    borderRadius: '12px',
    padding: '18px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  feedbackMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
    flexWrap: 'wrap',
  },
  feedbackMessage: {
    fontSize: '14px',
    color: '#b0bcda',
    fontStyle: 'italic',
    lineHeight: 1.6,
  },
  ratingBadge: {
    fontSize: '13px',
    color: '#f59e0b',
    letterSpacing: '2px',
  },
  lessonTag: {
    fontSize: '11px',
    padding: '2px 8px',
    backgroundColor: '#0e1828',
    border: '1px solid #1e2a3a',
    borderRadius: '4px',
    color: '#6b7a99',
  },
};
